import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { COMPANY } from "@/lib/compliance";
import {
    CreditCard,
    ShieldCheck,
    AlertCircle,
    CheckCircle2,
    Loader2,
    DollarSign,
    FileText,
} from "lucide-react";

/**
 * Premium Payments page — custom interface around PayPal Smart Buttons.
 *
 * The original implementation used PayPal Hosted Buttons, which always
 * routes the customer to a Login-first checkout screen and renders an
 * unlabeled "optional note" textarea. Both were confusing for clients.
 *
 * Smart Buttons let us:
 *   - Render our own dark/gold amount + memo inputs on the page
 *   - Dynamically create the order from those inputs
 *   - Send the customer DIRECTLY to a card-entry screen when they click
 *     "Pay with Debit or Credit Card" (no PayPal login required)
 *   - Show a branded "Payment Successful" state inline after capture
 */

const PAYPAL_CLIENT_ID =
    process.env.REACT_APP_PAYPAL_CLIENT_ID ||
    "BAAnC0XB9iewiO40e1WSa9PCyjmTX0Ho3pilpFMq0pC0BKIY0-uqWhhuEO23rf6gFZ1v7jFZK1ok-gNN6w";
const PAYPAL_SDK_SRC = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=buttons&enable-funding=venmo&currency=USD&intent=capture`;

/**
 * Load the PayPal SDK exactly once per browser session and return a
 * Promise that resolves when `window.paypal.Buttons` is callable.
 * Survives React 18 StrictMode double-mount via a window-scoped cache.
 */
function loadPaypalSDK() {
    if (window.__endurancePaypalReady) return window.__endurancePaypalReady;
    window.__endurancePaypalReady = new Promise((resolve, reject) => {
        if (window.paypal && window.paypal.Buttons) return resolve();
        const existing = document.querySelector(
            'script[data-paypal-sdk="endurance-smart"]'
        );
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", reject);
            return;
        }
        const script = document.createElement("script");
        script.src = PAYPAL_SDK_SRC;
        script.dataset.paypalSdk = "endurance-smart";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
    });
    return window.__endurancePaypalReady;
}

const AMOUNT_PATTERN = /^\d{1,7}(\.\d{0,2})?$/;

export default function PaymentsPage() {
    const [amount, setAmount] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [memo, setMemo] = useState("");
    const [sdkStatus, setSdkStatus] = useState("loading"); // loading | ready | error
    const [paymentError, setPaymentError] = useState(null);
    const [success, setSuccess] = useState(null);
    const buttonsHostRef = useRef(null);
    const buttonsInstanceRef = useRef(null);

    const parsedAmount = parseFloat(amount);
    const amountIsValid =
        AMOUNT_PATTERN.test(amount) && parsedAmount > 0 && parsedAmount < 10000000;

    // Load PayPal SDK on mount
    useEffect(() => {
        let cancelled = false;
        loadPaypalSDK()
            .then(() => !cancelled && setSdkStatus("ready"))
            .catch(() => !cancelled && setSdkStatus("error"));
        return () => {
            cancelled = true;
        };
    }, []);

    // Render (or re-render) Smart Buttons whenever amount/memo changes
    useEffect(() => {
        if (sdkStatus !== "ready") return;
        if (!buttonsHostRef.current) return;
        if (!amountIsValid) {
            buttonsHostRef.current.innerHTML = "";
            return;
        }

        // Tear down any previous instance
        try {
            buttonsInstanceRef.current?.close?.();
        } catch (_) {
            /* noop */
        }
        buttonsHostRef.current.innerHTML = "";

        const instance = window.paypal.Buttons({
            style: {
                layout: "vertical",
                color: "gold",
                shape: "pill",
                label: "pay",
                tagline: false,
            },
            createOrder: (_data, actions) =>
                actions.order.create({
                    intent: "CAPTURE",
                    purchase_units: [
                        {
                            amount: {
                                value: parsedAmount.toFixed(2),
                                currency_code: "USD",
                            },
                            description: `Insurance premium payment to ${COMPANY.shortName}`,
                            invoice_id: invoiceNumber
                                ? invoiceNumber.slice(0, 127)
                                : undefined,
                            custom_id: memo ? memo.slice(0, 127) : undefined,
                        },
                    ],
                    application_context: {
                        brand_name: COMPANY.shortName,
                        shipping_preference: "NO_SHIPPING",
                        user_action: "PAY_NOW",
                    },
                }),
            onApprove: async (_data, actions) => {
                try {
                    const details = await actions.order.capture();
                    setSuccess({
                        orderId: details?.id,
                        amount: parsedAmount.toFixed(2),
                        invoiceNumber,
                        memo,
                        payerEmail: details?.payer?.email_address,
                        payerName: details?.payer?.name?.given_name,
                    });
                    setPaymentError(null);
                } catch (_e) {
                    setPaymentError(
                        "PayPal accepted the payment authorization but the capture step failed. Please call us at " +
                            COMPANY.phone +
                            " before retrying so we can verify whether your card was charged."
                    );
                }
            },
            onError: () =>
                setPaymentError(
                    "PayPal could not process this payment. Please try again, or call us at " +
                        COMPANY.phone +
                        " for assistance."
                ),
            onCancel: () => setPaymentError(null),
        });
        buttonsInstanceRef.current = instance;
        instance.render(buttonsHostRef.current).catch(() =>
            setPaymentError(
                "We couldn't load the secure payment buttons. Please refresh the page or call " +
                    COMPANY.phone +
                    "."
            )
        );

        return () => {
            try {
                instance.close?.();
            } catch (_) {
                /* noop */
            }
        };
    }, [sdkStatus, amount, invoiceNumber, memo, amountIsValid, parsedAmount]);

    const handleAmountChange = (raw) => {
        // Allow only digits with up to two decimal places
        const cleaned = raw.replace(/[^\d.]/g, "");
        const parts = cleaned.split(".");
        let next = parts[0];
        if (parts.length > 1) next += "." + parts.slice(1).join("").slice(0, 2);
        setAmount(next);
    };

    if (success) {
        return (
            <PageShell
                testId="page-payments"
                kicker="Premium Payments"
                title="Payment Successful"
            >
                <div
                    data-testid="payment-success-card"
                    className="not-prose rounded-2xl border border-[rgba(212,175,55,0.45)] bg-gradient-to-br from-[#0a1426]/60 via-[#0b0e13] to-[#0b0e13] p-7 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
                >
                    <div className="flex items-start gap-4">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-brand-gold/60 bg-[#0d1117]">
                            <CheckCircle2
                                aria-hidden="true"
                                className="h-6 w-6 text-brand-gold2"
                            />
                        </span>
                        <div>
                            <p className="font-serif text-2xl text-brand-ivory">
                                Thank you{success.payerName ? `, ${success.payerName}` : ""}.
                            </p>
                            <p className="mt-2 text-sm leading-relaxed text-brand-ivory/85">
                                Your payment of{" "}
                                <strong className="text-brand-gold2">
                                    ${success.amount} USD
                                </strong>{" "}
                                was successfully captured by PayPal.
                            </p>
                            <dl className="mt-4 grid gap-2 text-xs text-brand-muted sm:grid-cols-[auto_1fr] sm:gap-x-4">
                                <dt className="text-brand-goldSoft">PayPal Order ID</dt>
                                <dd className="font-mono text-brand-ivory/90">
                                    {success.orderId}
                                </dd>
                                {success.invoiceNumber && (
                                    <>
                                        <dt className="text-brand-goldSoft">
                                            Invoice Number
                                        </dt>
                                        <dd className="font-mono text-brand-ivory/90">
                                            {success.invoiceNumber}
                                        </dd>
                                    </>
                                )}
                                {success.memo && (
                                    <>
                                        <dt className="text-brand-goldSoft">Memo</dt>
                                        <dd className="text-brand-ivory/90">
                                            {success.memo}
                                        </dd>
                                    </>
                                )}
                                {success.payerEmail && (
                                    <>
                                        <dt className="text-brand-goldSoft">Receipt sent to</dt>
                                        <dd className="text-brand-ivory/90">
                                            {success.payerEmail}
                                        </dd>
                                    </>
                                )}
                            </dl>
                        </div>
                    </div>
                </div>

                <p className="mt-6 text-base leading-relaxed">
                    PayPal will email you a receipt at the address used to
                    pay. {COMPANY.shortName} will follow up separately to
                    confirm application of the payment to your policy when
                    appropriate.
                </p>
                <p>
                    Remember: payment does <strong>not</strong> by itself
                    create, bind, modify, or guarantee insurance coverage.
                    Coverage is only in force when expressly confirmed in
                    writing by an authorized insurer or licensed producer.
                </p>
                <p>
                    Questions? Call{" "}
                    <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a> or
                    email <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                </p>
                <button
                    type="button"
                    data-testid="payment-make-another"
                    onClick={() => {
                        setSuccess(null);
                        setAmount("");
                        setMemo("");
                    }}
                    className="not-prose mt-4 inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    Make another payment
                </button>
            </PageShell>
        );
    }

    return (
        <PageShell
            testId="page-payments"
            kicker="Premium Payments"
            title="Pay Your Insurance Premium"
            seoDescription="Pay your Endurance Casualty insurance premium securely online via PayPal or credit/debit card. Same-day confirmation; carrier-direct payments handled separately by your assigned licensed agent."
        >
            <p className="text-base leading-relaxed text-brand-muted">
                Enter your premium amount and (optionally) an invoice number
                or memo, then choose how you&apos;d like to pay. PayPal
                processes the payment securely &mdash; you can pay with any
                major debit or credit card, with PayPal, or with Venmo.{" "}
                <strong className="text-brand-ivory">
                    No PayPal account is required to pay with a card.
                </strong>
            </p>

            {/* Secure checkout card */}
            <div
                data-testid="payment-card"
                className="not-prose mt-6 rounded-2xl border border-[rgba(212,175,55,0.3)] bg-[#0b0e13] p-5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)] md:p-7"
            >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                    <CreditCard
                        aria-hidden="true"
                        className="h-4 w-4 text-brand-gold2"
                    />
                    Secure Premium Checkout
                </div>

                {/* Amount input */}
                <div className="mt-5">
                    <label
                        htmlFor="payment-amount"
                        className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft"
                    >
                        Premium Amount (USD) <span className="text-brand-gold2">*</span>
                    </label>
                    <div className="relative">
                        <DollarSign
                            aria-hidden="true"
                            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-goldSoft"
                        />
                        <input
                            id="payment-amount"
                            data-testid="payment-amount-input"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="0.00"
                            value={amount}
                            onChange={(e) => handleAmountChange(e.target.value)}
                            aria-required="true"
                            aria-invalid={amount.length > 0 && !amountIsValid}
                            className="w-full rounded-lg border border-[rgba(212,175,55,0.35)] bg-[#0d1117] px-10 py-3 font-mono text-lg text-brand-ivory placeholder-brand-muted/60 transition-colors focus:border-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                        />
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs uppercase tracking-[0.25em] text-brand-goldSoft">
                            USD
                        </span>
                    </div>
                    <p className="mt-2 text-[11px] text-brand-muted">
                        Enter the amount shown on your invoice or
                        declarations page. Not sure?{" "}
                        <a
                            href={`tel:${COMPANY.phoneTel}`}
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            Call {COMPANY.phone}
                        </a>{" "}
                        before paying.
                    </p>
                </div>

                {/* Invoice number (separate, prominent) */}
                <div className="mt-5">
                    <label
                        htmlFor="payment-invoice"
                        className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft"
                    >
                        Invoice Number{" "}
                        <span className="normal-case tracking-normal text-brand-muted/70">
                            (recommended)
                        </span>
                    </label>
                    <div className="relative">
                        <FileText
                            aria-hidden="true"
                            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-goldSoft"
                        />
                        <input
                            id="payment-invoice"
                            data-testid="payment-invoice-input"
                            type="text"
                            autoComplete="off"
                            placeholder="e.g. INV-2026-0145"
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            maxLength={127}
                            className="w-full rounded-lg border border-[rgba(212,175,55,0.35)] bg-[#0d1117] px-10 py-3 font-mono text-sm text-brand-ivory placeholder-brand-muted/60 transition-colors focus:border-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                        />
                    </div>
                    <p className="mt-2 text-[11px] text-brand-muted">
                        Copy the invoice number from your invoice or
                        declarations page. PayPal will include it on your
                        receipt and on our payment reports so we can match
                        the payment to your policy.
                    </p>
                </div>

                {/* Optional memo / note */}
                <div className="mt-5">
                    <label
                        htmlFor="payment-memo"
                        className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft"
                    >
                        Memo or Note{" "}
                        <span className="normal-case tracking-normal text-brand-muted/70">
                            (optional)
                        </span>
                    </label>
                    <div className="relative">
                        <FileText
                            aria-hidden="true"
                            className="pointer-events-none absolute left-4 top-3.5 h-4 w-4 text-brand-goldSoft"
                        />
                        <input
                            id="payment-memo"
                            data-testid="payment-memo-input"
                            type="text"
                            autoComplete="off"
                            placeholder="e.g. 'March policy renewal' or policy number"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            maxLength={127}
                            className="w-full rounded-lg border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-10 py-3 text-sm text-brand-ivory placeholder-brand-muted/60 transition-colors focus:border-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                        />
                    </div>
                    <p className="mt-2 text-[11px] text-brand-muted">
                        Anything else you want us to know about this
                        payment.
                    </p>
                </div>

                {/* Payment error banner (if any) */}
                {paymentError && (
                    <div
                        data-testid="payment-error"
                        role="alert"
                        className="mt-5 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm leading-relaxed text-red-200"
                    >
                        <AlertCircle
                            aria-hidden="true"
                            className="mt-0.5 h-4 w-4 shrink-0 text-red-300"
                        />
                        <span>{paymentError}</span>
                    </div>
                )}

                {/* PayPal Smart Buttons host */}
                <div className="mt-6">
                    <p className="mb-3 text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft">
                        Choose how to pay
                    </p>
                    {!amountIsValid && (
                        <p
                            data-testid="payment-amount-prompt"
                            className="rounded-lg border border-dashed border-[rgba(212,175,55,0.35)] bg-[#0d1117] px-4 py-6 text-center text-sm text-brand-muted"
                        >
                            Enter your premium amount above to display
                            payment options.
                        </p>
                    )}
                    {amountIsValid && sdkStatus === "loading" && (
                        <p className="flex items-center justify-center gap-2 rounded-lg bg-[#0d1117] px-4 py-6 text-sm text-brand-muted">
                            <Loader2
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin text-brand-gold2"
                            />
                            Loading secure payment buttons&hellip;
                        </p>
                    )}
                    {amountIsValid && sdkStatus === "error" && (
                        <p
                            data-testid="payment-sdk-error"
                            role="alert"
                            className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-200"
                        >
                            <AlertCircle
                                aria-hidden="true"
                                className="mt-0.5 h-4 w-4 shrink-0 text-red-300"
                            />
                            <span>
                                We couldn&apos;t reach PayPal&apos;s secure
                                checkout right now. Please refresh, or call{" "}
                                <a
                                    href={`tel:${COMPANY.phoneTel}`}
                                    className="font-semibold underline-offset-4 hover:underline"
                                >
                                    {COMPANY.phone}
                                </a>{" "}
                                to arrange a different payment channel.
                            </span>
                        </p>
                    )}
                    <div
                        ref={buttonsHostRef}
                        data-testid="paypal-smart-buttons"
                        aria-label="PayPal secure payment buttons"
                        className="paypal-smart min-h-[60px]"
                    />
                </div>

                <p className="mt-5 inline-flex items-center gap-2 text-[11px] text-brand-muted">
                    <ShieldCheck
                        aria-hidden="true"
                        className="h-3.5 w-3.5 text-brand-gold2"
                    />
                    Payment processed by PayPal Holdings, Inc. &bull;{" "}
                    {COMPANY.shortName} does not store payment-card numbers.
                </p>
            </div>

            <h2>Before You Pay</h2>
            <ul>
                <li>
                    Pay only the amount shown on your{" "}
                    <strong>invoice or declarations page</strong>. If you are
                    not sure of the amount, call us at{" "}
                    <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a>{" "}
                    before submitting payment.
                </li>
                <li>
                    Payment does <strong>not</strong> create, bind, modify,
                    or guarantee insurance coverage. Coverage is only in
                    force when expressly confirmed in writing by an
                    authorized insurer or licensed producer.
                </li>
                <li>
                    If a policy is being newly bound, replaced, or
                    reinstated, do <strong>not</strong> cancel any existing
                    coverage until you receive written confirmation from us
                    that the new or replacement coverage is in force.
                </li>
                <li>
                    After payment, you will receive a PayPal receipt at the
                    email address you used to pay. {COMPANY.shortName} will
                    follow up separately to confirm application of the
                    payment to your policy when appropriate.
                </li>
            </ul>

            <h2>How the PayPal Window Works</h2>
            <p>
                When you click the{" "}
                <strong>&ldquo;Debit or Credit Card&rdquo;</strong> button, a
                PayPal-secured payment screen opens and takes you directly
                to a card-entry form &mdash; no PayPal account or sign-in is
                required. PayPal returns to this page once the payment is
                complete and we&apos;ll display a confirmation here.
            </p>

            <h2>Third-Party Payment Processing</h2>
            <p>
                Premium payments are processed by{" "}
                <strong>PayPal Holdings, Inc.</strong>, a third-party payment
                processor, under PayPal&apos;s own terms, privacy notice,
                and security practices. {COMPANY.shortName} does not receive
                or store full payment-card numbers, bank-account numbers, or
                similar payment credentials. {COMPANY.shortName} receives
                confirmation of the payment along with the limited
                transaction information PayPal makes available to the
                merchant.
            </p>

            <h2>Need a Different Channel?</h2>
            <p>
                If you need to pay by check, ACH, or another channel, please
                call{" "}
                <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a> or
                email{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> and
                we will provide instructions for an appropriate channel.
                Please do not send Social Security numbers, payment-card
                numbers, bank-account numbers, or other highly sensitive
                information by unencrypted email.
            </p>

            <div className="not-prose mt-6 flex items-start gap-3 rounded-md border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.04)] px-4 py-3 text-xs leading-relaxed text-brand-ivory/85">
                <AlertCircle
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold2"
                />
                <span>
                    {COMPANY.fullLegalName} is an independent insurance
                    agency and not an insurance company. Coverage is not
                    bound unless confirmed in writing by an authorized
                    insurer or licensed producer.
                </span>
            </div>

            <h2>Related Documents</h2>
            <p>
                See our <Link to="/privacy">Privacy Notice</Link> and{" "}
                <Link to="/terms">Terms of Service</Link> for additional
                information about how we handle your information and the
                limitations of our services.
            </p>
        </PageShell>
    );
}
